/**
 * NoteReasonDialog - "why?" for every change to a note (docs/plans/crm-report-builder-search-collab-plan.md
 * A1.2, A1.4, A5 N5 a, B1.4; WP N-7): the five reasons as a radio list (Typo or wording, Wrong
 * information, Wrong patient, Duplicate, Other), a required sentence for Other (at most 500 characters),
 * then "Save change" or "Delete note". Nothing is picked for the user: every change says why.
 *
 * Also here, for X-3:
 *   - NoteDeleteDialog: the delete flow the menu's Delete opens. The reason, then
 *     POST /contacts/:id/notes/:noteId/delete with expected_edit_count; a refusal keeps the dialog open
 *     with the server's sentence; a teammate's edit in the meantime (409 note_changed) shows their text
 *     and turns the button into "Delete anyway", which sends their edit_count (or no count when the
 *     server gave none: a deliberate delete).
 *   - NoteConflictNotice: the 409 box ("Dana Smith edited this note just now" and their text) that
 *     NoteEditForm and NoteDeleteDialog both show.
 *
 * The dialog portals to <body> with font-inter re-applied, in the Z.modalRaised tier so it clears a
 * modal the thread may sit in. Clicks and keys inside never bubble to the React tree around the caller
 * (a note card, the edit form); Escape is handled in the bubble phase and stopped there. The dialog
 * takes the keyboard on the first reason and gives it back where it was on close. Its state resets on
 * every open.
 */
import { useEffect, useId, useRef, useState } from 'react';
import type { FormEvent, KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent, ReactNode, SyntheticEvent } from 'react';
import { createPortal } from 'react-dom';
import { Loader2 } from 'lucide-react';
import type { Communication } from '../types';
import { Alert, Z, btnDanger, btnGhost, btnPrimary, fieldCls } from '../components/ui';
import { deleteNote, noteErrorOf } from './api';
import { NOTE_REASONS, NOTE_REASON_TEXT_MAX, conflictWords } from './noteLogic';
import type { NoteWordsOptions } from './noteLogic';
import type { NoteConflict, NoteDeleteRequest, NoteReasonCode, NoteRetraction } from './types';

/** What the person picked: a reason, and the sentence Other needs. */
export interface NoteReasonChoice {
  reason_code: NoteReasonCode;
  /** Only with Other: trimmed, never blank, at most 500 characters. */
  reason_text?: string;
}

export interface NoteReasonDialogProps {
  open: boolean;
  /** 'edit' asks why the text is changing ("Save change"); 'delete' why it goes ("Delete note", red). */
  mode: 'edit' | 'delete';
  /**
   * The pick. It may return a promise: the dialog then shows a spinner until it settles and, when it
   * rejects, shows the failure's sentence (noteErrorOf) and stays open. The caller closes the dialog
   * (open = false) when it is done with it.
   */
  onConfirm: (reason: NoteReasonChoice) => void | Promise<unknown>;
  /** Cancel, Escape or a press on the backdrop (ignored while a promise is pending). */
  onCancel: () => void;
  /** Words under the title; each mode has its own default. */
  message?: ReactNode;
  /** The confirm button's words; "Save change" / "Delete note" by default. */
  confirmLabel?: string;
}

const TITLE = {
  edit: 'Why are you changing this note?',
  delete: 'Why are you deleting this note?',
};

const DEFAULT_MESSAGE = {
  edit: "The text before your change is kept in the note's history.",
  delete: 'The note becomes a grey marker on the timeline. Its words stay in its history, where only CRM admins can read them.',
};

const CONFIRM = { edit: 'Save change', delete: 'Delete note' };

const stopClick = (e: SyntheticEvent) => e.stopPropagation();

export default function NoteReasonDialog(props: NoteReasonDialogProps) {
  if (!props.open) return null;
  return <ReasonDialogBody {...props} />;
}

function ReasonDialogBody({ mode, onConfirm, onCancel, message, confirmLabel }: NoteReasonDialogProps) {
  const [code, setCode] = useState<NoteReasonCode | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const alive = useRef(true);
  const firstRef = useRef<HTMLInputElement>(null);
  const textRef = useRef<HTMLInputElement>(null);
  const groupName = useId();
  const titleId = useId();
  const textId = useId();

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // The keyboard lands on the first reason, and goes back where it was when the dialog closes.
  useEffect(() => {
    const before = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    firstRef.current?.focus();
    return () => {
      if (before && before.isConnected) before.focus();
    };
  }, []);

  const option = NOTE_REASONS.find((r) => r.code === code) || null;
  const needsText = Boolean(option?.needsText);

  // Other: the sentence box takes the keyboard.
  useEffect(() => {
    if (needsText) textRef.current?.focus();
  }, [needsText]);

  const sentence = text.trim();
  const ready = Boolean(option) && (!needsText || (sentence !== '' && sentence.length <= NOTE_REASON_TEXT_MAX));

  const cancel = () => {
    if (!busy) onCancel();
  };

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!ready || busy || !code) return;
    const choice: NoteReasonChoice = needsText ? { reason_code: code, reason_text: sentence } : { reason_code: code };
    setError(null);
    let pending: unknown;
    try {
      pending = onConfirm(choice);
    } catch (err) {
      setError(noteErrorOf(err).message);
      return;
    }
    if (!pending || typeof (pending as Promise<unknown>).then !== 'function') return;
    setBusy(true);
    try {
      await pending;
    } catch (err) {
      if (alive.current) setError(noteErrorOf(err).message);
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  const onKeyDown = (e: ReactKeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
    }
  };

  const onBackdrop = (e: ReactMouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) cancel();
  };

  const danger = mode === 'delete';
  const label = confirmLabel || CONFIRM[mode];

  return createPortal(
    <div className="font-inter" onClick={stopClick} onKeyDown={onKeyDown}>
      <div
        className={`fixed inset-0 ${Z.modalRaised} flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm`}
        onMouseDown={onBackdrop}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          data-note-reason={mode}
          className="my-auto w-full max-w-sm rounded-xl bg-white p-5 shadow-xl dark:bg-gray-800"
        >
          <form onSubmit={submit} noValidate>
            <h2 id={titleId} className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {TITLE[mode]}
            </h2>
            <div className="mt-1 text-sm text-gray-600 dark:text-gray-300">{message ?? DEFAULT_MESSAGE[mode]}</div>

            <fieldset className="mt-3" disabled={busy}>
              <legend className="sr-only">Reason</legend>
              <div className="space-y-1.5">
                {NOTE_REASONS.map((r, i) => {
                  const checked = code === r.code;
                  return (
                    <label
                      key={r.code}
                      className={`flex min-h-[44px] cursor-pointer items-center gap-2.5 rounded-lg border px-3 text-sm transition-colors duration-150 focus-within:ring-2 focus-within:ring-primary-500 sm:min-h-0 sm:py-1.5 ${
                        checked
                          ? 'border-primary-600 bg-primary-50 font-medium text-primary-700 dark:border-primary-500 dark:bg-primary-900/20 dark:text-primary-300'
                          : 'border-gray-200 text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-700/50'
                      }`}
                    >
                      <input
                        ref={i === 0 ? firstRef : undefined}
                        type="radio"
                        name={groupName}
                        value={r.code}
                        checked={checked}
                        onChange={() => setCode(r.code)}
                        className="h-4 w-4 shrink-0 accent-primary-600 focus:outline-none"
                      />
                      {r.label}
                    </label>
                  );
                })}
              </div>
              {needsText && (
                <div className="mt-2">
                  <label htmlFor={textId} className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                    Say why in a sentence (required)
                  </label>
                  <input
                    id={textId}
                    ref={textRef}
                    type="text"
                    value={text}
                    maxLength={NOTE_REASON_TEXT_MAX}
                    onChange={(e) => setText(e.target.value)}
                    className={`${fieldCls} min-h-[44px] w-full sm:min-h-0`}
                  />
                </div>
              )}
            </fieldset>

            {error && (
              <Alert tone="error" size="sm" className="mt-3">
                {error}
              </Alert>
            )}

            <div className="mt-5 flex items-center justify-end gap-3">
              <button type="button" onClick={cancel} disabled={busy} className={btnGhost}>
                Cancel
              </button>
              <button type="submit" disabled={!ready || busy} className={danger ? btnDanger : btnPrimary}>
                {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
                {label}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------------------------
// The 409 box (plan A1.4: "Dana edited this note just now"; the recruitment list's rule)
// ---------------------------------------------------------------------------------------------

export interface NoteConflictNoticeProps {
  conflict: NoteConflict;
  nameOf?: NoteWordsOptions['nameOf'];
  /** Under their text: NoteEditForm's Keep theirs / Save mine. */
  children?: ReactNode;
}

/** "Dana Smith edited this note just now", then their text as it is now. */
export function NoteConflictNotice({ conflict, nameOf, children }: NoteConflictNoticeProps) {
  const theirs = conflict.current.body;
  return (
    <Alert tone="warning" size="sm" title={conflictWords(conflict, { nameOf })}>
      <p className="mt-1 text-[10px] font-semibold uppercase tracking-widest">Their version</p>
      <p
        data-note-conflict-text=""
        className="mt-0.5 max-h-32 overflow-y-auto whitespace-pre-wrap break-words rounded-md bg-white/70 px-2 py-1.5 text-gray-800 dark:bg-gray-900/40 dark:text-gray-200"
      >
        {theirs || '(no text)'}
      </p>
      {children}
    </Alert>
  );
}

// ---------------------------------------------------------------------------------------------
// The delete flow (plan A1.3 steps 3 and 5; decisions N2 a, N3 a, N5 a)
// ---------------------------------------------------------------------------------------------

export interface NoteDeleteDialogProps {
  open: boolean;
  contactId: string;
  /** The typed note being deleted: its id, and the edit_count the menu was opened on. */
  note: Communication;
  /**
   * The note as the server answered it (body '', deleted_at and deleted_by set) and what was withdrawn
   * from the profile (the counts, 'failed', or null when the server sent none). The caller patches
   * the thread (patchCommunication) and closes the dialog.
   */
  onDeleted: (note: Communication, retraction: NoteRetraction | null) => void;
  /** Cancel, Escape or the backdrop. */
  onClose: () => void;
  /** A user id to a name, for the 409 line (useTeam().byId); a name the server sent wins. */
  nameOf?: NoteWordsOptions['nameOf'];
}

export function NoteDeleteDialog(props: NoteDeleteDialogProps) {
  if (!props.open) return null;
  return <DeleteFlow {...props} />;
}

const editCountOf = (c: Pick<Communication, 'edit_count'>): number =>
  typeof c.edit_count === 'number' && Number.isFinite(c.edit_count) ? c.edit_count : 0;

function DeleteFlow({ contactId, note, onDeleted, onClose, nameOf }: NoteDeleteDialogProps) {
  const [expected, setExpected] = useState<number | null>(() => editCountOf(note));
  const [conflict, setConflict] = useState<NoteConflict | null>(null);

  const confirm = async (reason: NoteReasonChoice) => {
    const req: NoteDeleteRequest = { ...reason };
    if (expected !== null) req.expected_edit_count = expected;
    try {
      const res = await deleteNote(contactId, note.id, req);
      onDeleted(res.note ?? { ...note, body: '', deleted_at: new Date().toISOString() }, res.retraction);
    } catch (e) {
      const err = noteErrorOf(e);
      if (err.code === 'note_changed' && err.conflict) {
        // Show their text; the next press deletes what they wrote (their count, or none: deliberate).
        setConflict(err.conflict);
        setExpected(err.conflict.current.edit_count);
        return;
      }
      throw e;
    }
  };

  return (
    <NoteReasonDialog
      open
      mode="delete"
      onConfirm={confirm}
      onCancel={onClose}
      confirmLabel={conflict ? 'Delete anyway' : undefined}
      message={
        conflict ? (
          <NoteConflictNotice conflict={conflict} nameOf={nameOf}>
            <p className="mt-1.5">Read their version first, or delete it anyway.</p>
          </NoteConflictNotice>
        ) : undefined
      }
    />
  );
}
